import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { collection, addDoc, doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { uploadMultiple } from "../utils/cloudinary";
import { CATEGORIES, SUBCATEGORIES_BY_CATEGORY } from "../utils/format";
import { ImagePlus, X, ArrowLeft, Truck } from "lucide-react";
import toast from "react-hot-toast";

export default function AdminAddProductPage() {
  const { id } = useParams(); // if editing
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [form, setForm] = useState({
    name: "", category: "", subcategory: "", description: "", price: "", originalPrice: "",
    stock: "", brand: "", featured: false, status: "active",
    specs: "", tags: "",
    shippingType: "standard", // standard | free | fixed | surcharge
    shippingFee: "",
    shippingSurcharge: "",
  });

  useEffect(() => {
    if (isEdit) {
      getDoc(doc(db, "products", id)).then(snap => {
        if (snap.exists()) {
          const d = snap.data();
          setForm({
            name: d.name || "", category: d.category || "", subcategory: d.subcategory || "", description: d.description || "",
            price: d.price || "", originalPrice: d.originalPrice || "",
            stock: d.stock ?? "", brand: d.brand || "", featured: d.featured || false,
            status: d.status || "active",
            specs: d.specs ? Object.entries(d.specs).map(([k, v]) => `${k}: ${v}`).join("\n") : "",
            tags: (d.tags || []).join(", "),
            shippingType: d.shippingType || "standard",
            shippingFee: d.shippingFee ?? "",
            shippingSurcharge: d.shippingSurcharge ?? "",
          });
          setExistingImages(d.images || []);
        }
      });
    }
  }, [id, isEdit]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  function handleFiles(e) {
    const selected = Array.from(e.target.files).slice(0, 5 - existingImages.length);
    setFiles(selected);
    setPreviews(selected.map(f => URL.createObjectURL(f)));
  }

  function removeExistingImage(idx) {
    setExistingImages(imgs => imgs.filter((_, i) => i !== idx));
  }

  // Parse "Key: Value" spec lines into object
  function parseSpecs(text) {
    const result = {};
    text.split("\n").forEach(line => {
      const [k, ...v] = line.split(":");
      if (k && v.length) result[k.trim()] = v.join(":").trim();
    });
    return result;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      let imageUrls = [...existingImages];
      if (files.length > 0) {
        const uploads = await uploadMultiple(files, "afriport/products");
        imageUrls = [...imageUrls, ...uploads.map(u => u.url)];
      }

      const data = {
        name: form.name,
        category: form.category,
        subcategory: form.subcategory || "",
        description: form.description,
        price: Number(form.price),
        originalPrice: form.originalPrice ? Number(form.originalPrice) : null,
        stock: Number(form.stock) || 0,
        brand: form.brand,
        featured: form.featured,
        status: form.status,
        images: imageUrls,
        specs: form.specs ? parseSpecs(form.specs) : {},
        tags: form.tags ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
        shippingType: form.shippingType || "standard",
        shippingFee: form.shippingFee !== "" && form.shippingFee !== null ? Number(form.shippingFee) : null,
        shippingSurcharge: form.shippingSurcharge !== "" && form.shippingSurcharge !== null ? Number(form.shippingSurcharge) : null,
        updatedAt: serverTimestamp(),
      };

      if (isEdit) {
        await updateDoc(doc(db, "products", id), data);
        toast.success("Product updated!");
      } else {
        data.createdAt = serverTimestamp();
        data.rating = 0;
        data.reviewCount = 0;
        await addDoc(collection(db, "products"), data);
        toast.success("Product added!");
      }
      navigate("/admin/products");
    } catch (err) {
      toast.error("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ padding: "40px 48px", maxWidth: 1000, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 32 }}>
        <button onClick={() => navigate("/admin/products")} style={{ border: "none", background: "var(--surface)", width: 40, height: 40, borderRadius: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-muted)", transition: "all 0.2s" }}
          onMouseEnter={e => { e.currentTarget.style.background = "#E2E8F0"; e.currentTarget.style.color = "var(--text)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "var(--surface)"; e.currentTarget.style.color = "var(--text-muted)"; }}>
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 style={{ fontFamily: "var(--font-head)", fontSize: 28, fontWeight: 800, color: "var(--text)" }}>{isEdit ? "Edit Product" : "Add New Product"}</h1>
          <p style={{ color: "var(--text-muted)", fontSize: 15, marginTop: 4 }}>{isEdit ? "Update product details and media." : "Create a new product listing in your store."}</p>
        </div>
      </div>

      <div className="card" style={{ padding: 40, background: "#fff", border: "1px solid var(--border)", boxShadow: "var(--shadow-sm)" }}>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 28 }}>
          <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={{ fontSize: 13, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Product Name *</label>
            <input className="form-input" required value={form.name} onChange={set("name")} placeholder="Full product name" style={{ padding: "12px 16px", borderRadius: 10, fontSize: 15 }} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))", gap: 20 }}>
            <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Category *</label>
              <select
                className="form-select"
                required
                value={form.category}
                onChange={e => {
                  const newCat = e.target.value;
                  setForm(f => ({ ...f, category: newCat, subcategory: "" }));
                }}
                style={{ padding: "12px 16px", borderRadius: 10, fontSize: 15 }}
              >
                <option value="">Select category</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Sub-Category</label>
              <select
                className="form-select"
                value={form.subcategory}
                onChange={set("subcategory")}
                disabled={!form.category || !(SUBCATEGORIES_BY_CATEGORY[form.category]?.length)}
                style={{ padding: "12px 16px", borderRadius: 10, fontSize: 15, opacity: !form.category ? 0.6 : 1 }}
              >
                <option value="">Select sub-category (optional)</option>
                {(SUBCATEGORIES_BY_CATEGORY[form.category] || []).map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Brand</label>
              <input className="form-input" value={form.brand} onChange={set("brand")} placeholder="e.g. Dell, Victron, Starlink" style={{ padding: "12px 16px", borderRadius: 10, fontSize: 15 }} />
            </div>
          </div>

          <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={{ fontSize: 13, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Description *</label>
            <textarea className="form-input" rows={5} required value={form.description} onChange={set("description")} style={{ resize: "vertical", padding: "16px", borderRadius: 10, fontSize: 15, lineHeight: 1.6 }} placeholder="Detailed product description..." />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))", gap: 20 }}>
            <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Selling Price (₦) *</label>
              <input className="form-input" type="number" min={0} required value={form.price} onChange={set("price")} placeholder="150000" style={{ padding: "12px 16px", borderRadius: 10, fontSize: 15 }} />
            </div>
            <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Compare Price (₦)</label>
              <input className="form-input" type="number" min={0} value={form.originalPrice} onChange={set("originalPrice")} placeholder="180000" style={{ padding: "12px 16px", borderRadius: 10, fontSize: 15 }} />
            </div>
            <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Stock Qty *</label>
              <input className="form-input" type="number" min={0} required value={form.stock} onChange={set("stock")} placeholder="10" style={{ padding: "12px 16px", borderRadius: 10, fontSize: 15 }} />
            </div>
          </div>

          {/* Shipping & Delivery Rules Card */}
          <div style={{ padding: 24, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <Truck size={18} color="var(--brand)" />
              <h4 style={{ fontFamily: "var(--font-head)", fontSize: 16, fontWeight: 700, margin: 0 }}>Product Shipping & Delivery Rules</h4>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: form.shippingType === "standard" || form.shippingType === "free" ? "1fr" : "repeat(auto-fit, minmax(min(100%, 240px), 1fr))", gap: 20 }}>
              <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Delivery Rule</label>
                <select className="form-select" value={form.shippingType} onChange={set("shippingType")} style={{ padding: "12px 16px", borderRadius: 10, fontSize: 14 }}>
                  <option value="standard">Standard (Calculated by customer's delivery state)</option>
                  <option value="free">Free Nationwide Shipping (Waives delivery fee)</option>
                  <option value="fixed">Fixed Delivery Fee (Flat shipping rate per unit)</option>
                  <option value="surcharge">Heavy / Bulky Item Surcharge (Extra freight fee)</option>
                </select>
              </div>

              {form.shippingType === "fixed" && (
                <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Fixed Shipping Fee (₦) *</label>
                  <input className="form-input" type="number" min={0} required value={form.shippingFee} onChange={set("shippingFee")} placeholder="e.g. 2500" style={{ padding: "12px 16px", borderRadius: 10, fontSize: 14 }} />
                  <span style={{ fontSize: 12, color: "var(--text-muted)" }}>This specific delivery fee will be applied to this product.</span>
                </div>
              )}

              {form.shippingType === "surcharge" && (
                <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Extra Freight Surcharge (₦) *</label>
                  <input className="form-input" type="number" min={0} required value={form.shippingSurcharge} onChange={set("shippingSurcharge")} placeholder="e.g. 5000" style={{ padding: "12px 16px", borderRadius: 10, fontSize: 14 }} />
                  <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Added on top of state delivery fee (ideal for heavy batteries, solar panels, inverters).</span>
                </div>
              )}
            </div>
          </div>

          <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={{ fontSize: 13, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Key Specifications (One per line: "Key: Value")</label>
            <textarea className="form-input" rows={4} value={form.specs} onChange={set("specs")} style={{ resize: "vertical", fontFamily: "monospace", fontSize: 14, padding: "16px", borderRadius: 10 }}
              placeholder={"Capacity: 200Ah\nVoltage: 12V\nWeight: 24kg\nWarranty: 2 Years"} />
          </div>

          <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={{ fontSize: 13, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Tags (comma-separated)</label>
            <input className="form-input" value={form.tags} onChange={set("tags")} placeholder="inverter, lithium, solar, 12v" style={{ padding: "12px 16px", borderRadius: 10, fontSize: 15 }} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))", gap: 20 }}>
            <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Status</label>
              <select className="form-select" value={form.status} onChange={set("status")} style={{ padding: "12px 16px", borderRadius: 10, fontSize: 15 }}>
                <option value="active">Active (visible in shop)</option>
                <option value="inactive">Hidden</option>
                <option value="draft">Draft</option>
              </select>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, paddingTop: 26 }}>
              <input type="checkbox" id="featured" checked={form.featured} onChange={set("featured")} style={{ width: 20, height: 20, cursor: "pointer", accentColor: "var(--brand)" }} />
              <label htmlFor="featured" style={{ cursor: "pointer", fontSize: 15, fontWeight: 600, color: "var(--text)" }}>Mark as Featured ⭐</label>
            </div>
          </div>

          {/* Image upload */}
          <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={{ fontSize: 13, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Product Images {isEdit ? "(add more)" : "(up to 5)"}</label>

            {/* Existing images */}
            {existingImages.length > 0 && (
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
                {existingImages.map((img, i) => (
                  <div key={i} style={{ position: "relative", width: 96, height: 96 }}>
                    <img src={img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 12, border: "2px solid var(--border)", boxShadow: "var(--shadow-sm)" }} />
                    <button type="button" onClick={() => removeExistingImage(i)}
                      style={{ position: "absolute", top: -8, right: -8, width: 24, height: 24, borderRadius: "50%", background: "#EF4444", border: "2px solid #fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", transition: "transform 0.2s" }}
                      onMouseEnter={e => e.currentTarget.style.transform = "scale(1.15)"}
                      onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <label style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: "32px", border: "2px dashed var(--border)", borderRadius: 16, cursor: "pointer", background: "var(--surface)", transition: "all 0.2s" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--brand)"; e.currentTarget.style.background = "var(--brand-light)"; }} 
              onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.background = "var(--surface)"; }}>
              <input type="file" accept="image/*" multiple onChange={handleFiles} style={{ display: "none" }} />
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "var(--shadow-sm)" }}>
                <ImagePlus size={24} color="var(--brand)" />
              </div>
              <span style={{ fontSize: 15, fontWeight: 600, color: "var(--text)" }}>Click to upload images</span>
              <span style={{ fontSize: 13, color: "var(--text-muted)" }}>JPEG, PNG, WEBP (Max 5MB per file)</span>
            </label>

            {previews.length > 0 && (
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 12 }}>
                {previews.map((src, i) => (
                  <img key={i} src={src} alt="" style={{ width: 96, height: 96, objectFit: "cover", borderRadius: 12, border: "2px solid var(--border)", boxShadow: "var(--shadow-sm)" }} />
                ))}
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 16, marginTop: 16, paddingTop: 32, borderTop: "1px solid var(--border)" }}>
            <button type="button" onClick={() => navigate("/admin/products")} className="btn btn-secondary" style={{ padding: "14px 24px", fontSize: 16 }}>Cancel</button>
            <button type="submit" disabled={loading} className="btn btn-primary btn-lg" style={{ flex: 1, padding: "14px 24px", fontSize: 16 }}>
              {loading ? "Saving…" : isEdit ? "Update Product" : "Add Product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
